import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Row, Col, Card, Badge } from "react-bootstrap";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

import { Line } from "react-chartjs-2";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { WiHumidity, WiThermometer } from "react-icons/wi";
import { TbPackage } from "react-icons/tb";

import Api from "../../../../services/Api";
import { formatLocal } from '../../../../utilis/dateUtils';

// Registrar os componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels
);

const plugins = {
  afterDraw: function (chart) {
    if (chart.data.datasets.length > 0) {
      if (chart.data.datasets[0].data.length === 0) {
        var width = chart.width,
          height = chart.height,
          ctx = chart.ctx;
        ctx.restore();
        var fontSize = (height / 160).toFixed(2);
        ctx.font = fontSize + "em sans-serif";
        ctx.textBaseline = "top";
        var text = "Nenhuma Informação Disponível",
          textX = Math.round((width - ctx.measureText(text).width) / 2),
          textY = height / 2;
        ctx.fillText(text, textX, textY);
        ctx.save();
      }
    }
  },
}

let delayed;

const options = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    y: {
      beginAtZero: false,
      ticks: {
        precision: 1,
        color: '#b0b0b0',
        font: {
          size: 11
        }
      },
      grid: {
        color: 'rgba(255, 255, 255, 0.1)',
        borderDash: [5, 5]
      }
    },
    x: {
      ticks: {
        maxRotation: 45,
        minRotation: 45,
        color: '#b0b0b0',
        font: {
          size: 10
        }
      },
      grid: {
        display: false
      }
    },
  },
  animations: {
    radius: {
      duration: 500,
      easing: 'linear',
      loop: (context) => context.active
    }
  },
  animation: {
    onComplete: () => {
      delayed = true;
    },
    delay: (context) => {
      let delay = 0;
      if (context.type === 'data' && context.mode === 'default' && !delayed) {
        delay = context.dataIndex * 5 + context.datasetIndex * 2;
      }
      return delay;
    },
  },
  hoverRadius: 8,
  hoverBackgroundColor: '#a4c639',
  interaction: {
    mode: 'nearest',
    intersect: false,
    axis: 'x'
  },
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      enabled: true,
      backgroundColor: 'rgba(40, 44, 52, 0.9)',
      titleColor: '#e0e0e0',
      bodyColor: '#e0e0e0',
      titleFont: {
        size: 12
      },
      bodyFont: {
        size: 11
      },
      padding: 10,
      cornerRadius: 8,
      displayColors: false,
      borderColor: 'rgba(164, 198, 57, 0.3)',
      borderWidth: 1
    },
    title: {
      display: true,
      text: '',
      font: {
        size: 14,
        weight: 'bold'
      },
      color: '#e0e0e0'
    },
    datalabels: {
      color: '#e0e0e0',
      font: {
        weight: 'bold',
        size: 12
      },
      // default: hide all labels, will enable only for last point via dataset config
      formatter: (value, context) => {
        return '';
      },
      align: 'center',
      offset: 0
    }
  }
};

let width, height, gradient;
function getGradient(ctx, chartArea) {
  const chartWidth = chartArea.right - chartArea.left;
  const chartHeight = chartArea.bottom - chartArea.top;
  if (!gradient || width !== chartWidth || height !== chartHeight) {
    width = chartWidth;
    height = chartHeight;
    gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);

    gradient.addColorStop(1, "#a4c639");
    gradient.addColorStop(0.7, "#3c834d");
    gradient.addColorStop(0.3, "#2a5e38");
    gradient.addColorStop(0, "#ff7e5f");
  }
  return gradient;
}

const GraficosFard = ({ serial, company, reload, detailsEquip, progressReset }) => {
  // receber a prop progressReset para reiniciar polling quando ProgressBar for reiniciado
  // assumimos que o parent (Dashboard) passará essa prop quando necessário
  // ...existing code...
  const { configs, equipments, _id } = company;

  const equipamento = useMemo(() => {
    return detailsEquip.find((equip) => equip?.serialnumber === serial);
  }, [serial, detailsEquip]);

  const getColSize = () => {
    if (equipments.length === 1) return 12;
    if (equipments.length <= 4) return 6;
    return 4;
  };

  const [medidas, setMedidas] = useState([]);
  const [status, setStatus] = useState(null);
  const [ultimaMedida, setUltimaMedida] = useState({
    _id: "",
    serialnumber: "",
    date: "",
    idcalib: "",
    grain: "",
    humidity: "",
    temperature: "",
    peso: "",
    freqc: "",
    freqv: "",
    ph: "",
    curvavalidade: "",
    companyId: "",
    localEquipament: ""
  });
  const [fardosPorHora, setFardosPorHora] = useState(null);  // Estado para armazenar fardos por hora (null = ainda não carregado)

  const handleEquipment = useCallback(async () => {
    try {
      // Buscando dados diretamente do sistema local em vez da API remota
      const response = await Api.get(`/api/v1/measurements/${serial}/${_id}`);
      console.debug(`[Grafico:${serial}] handleEquipment fetched`, response?.data?.length ?? response?.data);
      // Verifica se a resposta contém um status mensagem de "Não há medidas gravadas"
      if (response.data && response.data.status && response.data.status.includes("Não há medidas")) {
        console.log("Sem medidas disponíveis para este equipamento");
        setMedidas([]);
        return;
      }

      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        // Transformando os dados para o formato esperado
        const formattedData = response.data.map(item => ({
          _id: item.id || "",
          serialnumber: item.serialnumber || serial,
          date: item.date || "",
          humidity: parseFloat(item.humidity || item.umidade || "0"),
          temperature: parseFloat(item.temperature || item.temp || item.temperatura || "0"),
          grain: item.grain || "",
          freqc: item.freqc || "",
          freqv: item.freqv || item.freq_v || item.freq || "",
          ph: item.ph || "0"
        }));

        // Ordenando por data (mais recente primeiro)
        const orderedData = formattedData.sort((a, b) => {
          return new Date(a.date) - new Date(b.date);
        });

        setMedidas(orderedData);
        if (orderedData.length > 0) {
          setUltimaMedida(orderedData[orderedData.length - 1]);
        }
      } else if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        // Se a resposta for um objeto mas não um array, pode ser uma mensagem de erro ou um formato diferente
        console.log("Resposta em formato não esperado:", response.data);
      }
    } catch (error) {
      setMedidas([]);
      setUltimaMedida({
        _id: "",
        serialnumber: "",
        date: "",
        idcalib: "",
        grain: "",
        humidity: "",
        temperature: "",
        peso: "",
        freqc: "",
        freqv: "",
        ph: "",
        curvavalidade: "",
        companyId: "",
        localEquipament: ""
      });
      console.error('Erro ao buscar medidas do sistema local:', error?.response?.data || error?.message);
    }
  }, [serial, _id]);

  const handleGetStatus = useCallback(async () => {
    try {
      // Consultando status diretamente do sistema local
      const response = await Api.get(`/connection/status/serial/${serial}`);
      setStatus(response.data?.status);
    } catch (error) {
      console.error('Erro ao verificar status do dispositivo:', error?.message);
      setStatus("offline");
    }
  }, [serial]);

  // Função para obter fardos por hora quando for FARD
  function calcularEstimativaMedidasPorHora(medidas) {
    if (!Array.isArray(medidas) || medidas.length < 4) {
      return {
        success: true,
        estimativa_medidas_por_hora: 0,
        motivo: "poucas_medidas",
      };
    }

    try {
      // Ordenar as datas do mais recente para o mais antigo
      const datas = medidas
        .map((m) => {
          let d = m.date || m; // aceita array de objetos {date} ou strings
          if (d.endsWith("Z")) d = d.replace("Z", "");
          return new Date(d);
        })
        .filter((d) => !isNaN(d)) // remove inválidas
        .sort((a, b) => b - a);

      if (datas.length < 3) {
        return {
          success: true,
          estimativa_medidas_por_hora: 0,
          motivo: "poucas_medidas_validas",
        };
      }

      const agora = new Date();
      const minAtras = new Date(agora.getTime() - 70 * 60 * 1000);
      const ultima = datas[0];

      if (ultima < minAtras) {
        return {
          success: true,
          estimativa_medidas_por_hora: 0,
          ultima_medicao: ultima.toISOString(),
          motivo: "inativo",
        };
      }

      // Calcular intervalos entre medições consecutivas
      const intervalos = [];
      for (let i = 1; i < datas.length; i++) {
        const diff = (datas[i - 1] - datas[i]) / 1000; // segundos
        if (diff > 0) intervalos.push(diff);
      }

      if (intervalos.length === 0) {
        return {
          success: true,
          estimativa_medidas_por_hora: 0,
          motivo: "intervalos_invalidos",
        };
      }

      // Intervalo médio
      const intervaloMedio = intervalos.reduce((a, b) => a + b, 0) / intervalos.length;

      // Média móvel (últimos 10 intervalos, se houver)
      const n = 10;
      const ultimosIntervalos = intervalos.slice(-n);

      const mediaMovel = ultimosIntervalos.reduce((a, b) => a + b, 0) / ultimosIntervalos.length;
      const medidasMovelPorHora = 3600 / mediaMovel;

      return {
        success: true,
        estimativa_medidas_por_hora: Math.round(medidasMovelPorHora),
        intervalo_medio_segundos: Math.round(intervaloMedio * 100) / 100,
        media_movel_medidas_por_hora: Math.round(medidasMovelPorHora),
        calculos: {
          total_intervalos: intervalos.length,
          intervalos_utilizados: ultimosIntervalos.length,
        },
      };
    } catch (e) {
      console.error("Erro no cálculo:", e);
      return {
        success: false,
        estimativa_medidas_por_hora: 0,
        error: e.message,
      };
    }
  }

  // Atualiza sempre que 'medidas' mudar
  useEffect(() => {
    if (!medidas || medidas.length === 0) {
      setFardosPorHora(0);
      return;
    }

    const resultado = calcularEstimativaMedidasPorHora(medidas);
    setFardosPorHora(resultado?.estimativa_medidas_por_hora || 0);

  }, [medidas]);

  useEffect(() => {
    // buscar imediatamente quando reload ou serial mudarem
    handleEquipment();
    handleGetStatus();

  }, [reload, serial, handleEquipment, handleGetStatus]);

  // polling controlado pelo ProgressBar: quando progressReset muda, reinicia um polling de 30s
  const progressResetFirstRun = useRef(true);
  useEffect(() => {
    // Pula a primeira execução (mount). Só inicia polling quando progressReset é alterado depois do mount.
    if (progressResetFirstRun.current) {
      progressResetFirstRun.current = false;
      return;
    }

    let pollingInterval;

    // iniciar imediatamente uma chamada para garantir dados frescos quando a barra reiniciar
    handleEquipment();

    // configurar intervalo para executar a cada 30s
    pollingInterval = setInterval(() => {
      // usar a versão memorizada de handleEquipment
      try {
        handleEquipment();
        handleGetStatus();

        console.debug(`[Grafico:${serial}] polling triggered`);
      } catch (e) {
        console.error(`[Grafico:${serial}] polling error`, e);
      }
    }, 30000);

    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, [progressReset, handleEquipment, handleGetStatus, serial]);

  // Verificar se é um dispositivo FARD
  const isFardDevice = serial.includes('FARD');
  // mostrar coluna de FARD somente se for dispositivo FARD e houver valor (não undefined/null)
  const showFardColumn = isFardDevice && fardosPorHora !== null && fardosPorHora !== undefined;
  const metricColSize = showFardColumn ? 4 : 6;

  const isWithin75Minutes = (lastDateString) => {
    if (!lastDateString) return false;

    try {
      // Garantir formato ISO 8601 adequado
      const dateStr = lastDateString.replace("Z", "");

      const lastDate = new Date(dateStr);
      const now = new Date();

      // Verificar se a data é futura (pode indicar erro no relógio do servidor)
      if (lastDate > now) {
        console.log('Data futura detectada:', lastDateString, '- considerando como recente');
        return true; // Se a data for futura, consideraremos como recente
      }

      // Calcular a diferença em minutos
      const diffMs = now - lastDate;
      const diffMinutes = diffMs / (1000 * 60);

      // Verificar se está dentro de 1h15 (75 minutos)
      return diffMinutes <= 75;
    } catch (error) {
      console.error('Erro ao processar data:', lastDateString, error);
      return false;
    }
  };

  const isRecent = isWithin75Minutes(ultimaMedida.date);

  // calcular opções do chart dinamicamente para ajustar escala Y
  const chartOptions = useMemo(() => {
    // copiar opções base
    const opts = JSON.parse(JSON.stringify(options));

    // extrair valores de umidade atuais
    const values = medidas.map(m => (typeof m.humidity === 'number' ? m.humidity : Number(m.humidity || 0)));
    let dataMax = values.length ? Math.max(...values) : (configs?.maxHumidity ?? 0);
    let dataMin = values.length ? Math.min(...values) : (configs?.minHumidity ?? 0);

    // se não houver variação significativa, garantir um range visível
    const span = Math.max(0, dataMax - dataMin);

    let yMax, yMin;
    if (dataMax <= 15) {
      // exemplo pedido: para valores pequenos, mostrar 0..15
      yMax = 15;
      yMin = 0;
    } else {
      // aplicar padding proporcional (mínimo 1)
      const pad = Math.max(1, span * 0.5);
      yMax = Math.ceil(dataMax + pad);
      yMin = Math.floor(Math.max(0, dataMin - pad));
    }

    // garantir que yMin seja no máximo yMax-1
    if (yMin >= yMax) {
      yMin = Math.max(0, Math.floor(yMax - 1));
    }

    opts.scales = opts.scales || {};
    opts.scales.y = opts.scales.y || {};
    opts.scales.y.min = yMin;
    opts.scales.y.max = yMax;

    return opts;
  }, [medidas, configs]);

  return (
    <Col xs={12} sm={12} md={12} lg={12} xl={getColSize()} xxl={getColSize()} className="mb-1">
      <Card className="h-100 shadow dark-card" style={{ borderRadius: '16px', overflow: 'hidden', border: 'none', backgroundColor: '#282c34' }}>
        <Card.Body className="p-2">
          <Row className="g-3 mb-2">
            <Col md={4} className="text-center">
              <h6 className="mb-1 dark-text-secondary" style={{ fontSize: '0.8rem' }}>EQUIPAMENTO</h6>
              {status === "online" ? (
                <Badge bg="success" pill className="mb-2 px-3 py-2">ONLINE</Badge>
              ) : status === "unstable" ? (
                <Badge bg="warning" text="dark" pill className="mb-2 px-3 py-2">INSTÁVEL</Badge>
              ) : (
                <Badge bg="danger" pill className="mb-2 px-3 py-2">OFFLINE</Badge>
              )}

              <h5 className="mb-1 dark-text-accent" style={{ fontWeight: 600 }}>
                {serial || "--"}
              </h5>



            </Col>

            {ultimaMedida?.grain ?
              <Col md={4} className="text-center">
                <h6 className="mb-1 dark-text-secondary" style={{ fontSize: '0.8rem' }}>GRÃO</h6>
                <h5 className="mb-0 dark-text" style={{ fontWeight: 600 }}>
                  {ultimaMedida?.grain?.toUpperCase() || "--"}
                </h5>
              </Col>
              :
              <Col md={4} className="text-center">

              </Col>
            }

            <Col md={4} className="text-center">
              <h6 className="mb-1 dark-text-secondary" style={{ fontSize: '0.8rem' }}>ÚLTIMA MEDIDA</h6>
              <h5 className="mb-0" style={{ color: isRecent ? "#a4c639" : "#ff5252", fontWeight: 600 }}>
                {ultimaMedida?.date
                  ? formatLocal(ultimaMedida.date, undefined, {
                    day: '2-digit',
                    month: '2-digit',
                    year: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZone: 'UTC'
                  })
                  : "--"}
              </h5>

              <h6 className="mb-0 dark-text" style={{ fontWeight: 500 }}>
                {equipamento?.nameEquipament?.toUpperCase() || "--"}
              </h6>
            </Col>
          </Row>

          <Row className="mb-2 g-2">
            <Col md={metricColSize}>
              <Card className="mb-0 h-100 shadow-sm dark-card" style={{ borderRadius: '12px', border: 'none', backgroundColor: '#1e2128' }}>
                <Card.Body className="p-2 text-center">
                  <div className="d-flex align-items-center justify-content-center mb-2">
                    <WiHumidity size={30} color="#a4c639" />
                    <h6 style={{ color: "#a4c639", marginBottom: 0, marginLeft: '5px' }}>UMIDADE</h6>
                  </div>
                  <h1 style={{ color: "#e0e0e0", marginBottom: 0, fontWeight: 700 }}>
                    <span style={{ fontSize: "2.0rem" }}>
                      {ultimaMedida?.humidity ? Math.floor(ultimaMedida.humidity) : "--"}
                    </span>
                    <span style={{ fontSize: "1.5rem" }}>
                      {ultimaMedida?.humidity ? "." + (ultimaMedida.humidity % 1).toFixed(1).substring(2) : ""}
                    </span>
                    <span style={{ fontSize: "1.5rem", color: "#a4c639" }}>{ultimaMedida.humidity ? " %" : ""}</span>
                  </h1>
                </Card.Body>
              </Card>
            </Col>

            <Col md={metricColSize}>
              <Card className="mb-0 h-100 shadow-sm dark-card" style={{ borderRadius: '12px', border: 'none', backgroundColor: '#1e2128' }}>
                <Card.Body className="p-2 text-center">
                  <div className="d-flex align-items-center justify-content-center mb-2">
                    <WiThermometer size={30} color="#ff7e5f" />
                    <h6 style={{ color: "#ff7e5f", marginBottom: 0, marginLeft: '5px' }}>TEMPERATURA</h6>
                  </div>
                  <h1 style={{ color: "#e0e0e0", marginBottom: 0, fontWeight: 700 }}>
                    <span style={{ fontSize: "2.0rem" }}>
                      {ultimaMedida?.temperature ? Math.floor(ultimaMedida.temperature) : "--"}
                    </span>
                    <span style={{ fontSize: "1.5rem" }}>
                      {ultimaMedida?.temperature && "." + (ultimaMedida.temperature % 1).toFixed(1).substring(2)}
                    </span>
                    <span style={{ fontSize: "1.5rem", color: "#ff7e5f" }}>{ultimaMedida.temperature ? " °C" : ""}</span>
                  </h1>
                </Card.Body>
              </Card>
            </Col>
            {showFardColumn && (
              <Col md={4}>
                <Card className="mb-0 h-100 shadow-sm dark-card" style={{ borderRadius: '12px', border: 'none', backgroundColor: '#1e2128' }}>
                  <Card.Body className="p-2 text-center">
                    <div className="d-flex align-items-center justify-content-center mb-2">
                      <TbPackage size={24} color="#5c9edc" />
                      <h6 style={{ color: "#5c9edc", marginBottom: 0, marginLeft: '5px' }}>
                        FARDOS/HORA
                      </h6>
                    </div>
                    <h1 style={{ color: "#e0e0e0", marginBottom: 0, fontWeight: 700 }}>
                      <>
                        <span style={{ fontSize: "2.0rem" }}>
                          {(fardosPorHora !== null && fardosPorHora !== undefined) ? fardosPorHora : "--"}
                        </span>
                        <span style={{ fontSize: "1.2rem", color: "#b0b0b0" }}> fardos/h</span>
                      </>
                    </h1>
                  </Card.Body>
                </Card>
              </Col>
            )}
          </Row>

          <div style={{ height: 250, position: 'relative', backgroundColor: '#1e2128', borderRadius: '12px', padding: '10px' }}>
            <Line
              data={{
                labels: medidas.map(data =>
                  formatLocal(data.date, undefined, {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZone: 'UTC'
                  })
                ),
                datasets: [
                  {
                    label: 'UMIDADE',
                    backgroundColor: "rgba(60, 131, 77, 0.1)",
                    // provide numeric values so chart updates correctly
                    data: medidas.map((data) => Number(parseFloat(data?.humidity || 0).toFixed(1))),
                    borderColor: function (context) {
                      const chart = context.chart;
                      const { ctx, chartArea } = chart;
                      if (!chartArea) {
                        return;
                      }
                      return getGradient(ctx, chartArea);
                    },
                    borderWidth: 3,
                    fill: true,
                    // show a subtle dot only where there is a measurement
                    pointRadius: function (context) {
                      const v = context.dataset.data[context.dataIndex];
                      return v !== null && v !== undefined ? 3 : 0;
                    },
                    pointBackgroundColor: "#a4c639",
                    pointBorderColor: "#1e2128",
                    pointBorderWidth: 1.5,
                    pointHoverRadius: 6,
                    pointHoverBackgroundColor: "#a4c639",
                    pointHoverBorderColor: "#1e2128",
                    pointHoverBorderWidth: 2,
                    tension: 0.3,
                    datalabels: {
                      display: 'auto',
                      formatter: function (value) {
                        return value !== undefined && value !== null ? value : '';
                      },
                      color: '#e0e0e0',
                      backgroundColor: 'rgba(40, 44, 52, 0.65)',
                      borderRadius: 4,
                      padding: 4,
                      font: { size: 10, weight: 'bold' },
                      anchor: 'end',
                      align: 'top',
                      offset: -6
                    },
                  }
                ],
              }}
              plugins={[plugins]}
              options={chartOptions}
            />
          </div>
        </Card.Body>
      </Card>
    </Col >
  );
};

export default GraficosFard;
